/**
 * V2.0.81 — Tool-usage heatmap for the Admin Console.
 *
 * Queries `/api/admin/interaction-events` (rolls up the last 7 days by
 * default) and renders a compact `(tool, action) × count / commit-rate
 * / avg-ms` table. The single question this answers:
 *
 *   "Are people actually USING the wall tool, or did we ship a
 *    beautifully-refactored feature into the void?"
 *
 * Populated by the client-side `interactionTelemetry.js` batcher which
 * every FloorPlan2D interaction hook emits into.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BarChart3, RefreshCw } from "lucide-react";
import { getInteractionEvents } from "../lib/api";

const TOOL_LABELS = {
  wall_draw: "Wall drawing tool",
  wall_add_menu: "Wall-add menu",
  opening_drag: "Opening drag positioning",
  symbol_drag: "Symbol drag/move/resize",
  chat: "AI chat prompts",
};

function toolLabel(tool) {
  return TOOL_LABELS[tool] || tool || "—";
}

export default function ToolUsageCard() {
  const [rows, setRows] = useState([]);
  const [since, setSince] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hours, setHours] = useState(168);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getInteractionEvents(hours);
      setRows(r.data.rows || []);
      setSince(r.data.since_iso);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load telemetry");
    } finally {
      setLoading(false);
    }
  }, [hours]);

  useEffect(() => {
    load();
  }, [load]);

  const totalEvents = useMemo(
    () => rows.reduce((s, r) => s + (r.count || 0), 0),
    [rows],
  );

  // Group by tool so we can show a nice per-tool breakdown.
  const byTool = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const list = map.get(r.tool) || [];
      list.push(r);
      map.set(r.tool, list);
    }
    return Array.from(map.entries()).sort((a, b) => {
      const aSum = a[1].reduce((s, x) => s + x.count, 0);
      const bSum = b[1].reduce((s, x) => s + x.count, 0);
      return bSum - aSum;
    });
  }, [rows]);

  return (
    <div
      data-testid="tool-usage-card"
      className="border border-zinc-800 bg-zinc-950 p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-brass-400" />
          <h3 className="font-mono text-[11px] uppercase tracking-widest text-zinc-100">
            // Tool Usage Heatmap
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="tool-usage-hours-select"
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            className="text-[10px] bg-zinc-900 border border-zinc-700 text-zinc-300 px-2 py-1 font-mono"
          >
            <option value={24}>Last 24h</option>
            <option value={72}>Last 3 days</option>
            <option value={168}>Last 7 days</option>
            <option value={336}>Last 14 days</option>
            <option value={720}>Last 30 days</option>
          </select>
          <button
            type="button"
            onClick={load}
            data-testid="tool-usage-refresh"
            className="p-1 text-zinc-400 hover:text-zinc-100"
            aria-label="Refresh"
            disabled={loading}
          >
            <RefreshCw className={"w-3.5 h-3.5 " + (loading ? "animate-spin" : "")} />
          </button>
        </div>
      </div>

      <div className="text-[10px] font-mono text-zinc-500 mb-3">
        {since ? `Events since ${new Date(since).toLocaleString()}` : "—"} · total {totalEvents.toLocaleString()}
      </div>

      {error && (
        <div className="text-[11px] text-rose-400 mb-2">{error}</div>
      )}

      {!loading && rows.length === 0 && !error && (
        <div className="text-[11px] text-zinc-500 py-4 text-center border border-dashed border-zinc-800">
          No interaction events yet. Ship some tool clicks and check back.
        </div>
      )}

      {byTool.length > 0 && (
        <div className="space-y-3">
          {byTool.map(([tool, actions]) => {
            const total = actions.reduce((s, a) => s + a.count, 0);
            return (
              <div key={tool} data-testid={`tool-usage-group-${tool}`}>
                <div className="flex items-baseline justify-between mb-1">
                  <div className="font-mono text-[11px] text-zinc-200">
                    {toolLabel(tool)}
                  </div>
                  <div className="font-mono text-[10px] text-brass-400">
                    {total.toLocaleString()} events
                  </div>
                </div>
                <table className="w-full text-[10px] font-mono">
                  <thead>
                    <tr className="text-zinc-500 border-b border-zinc-800">
                      <th className="text-left font-normal py-1">action</th>
                      <th className="text-right font-normal py-1">count</th>
                      <th className="text-right font-normal py-1">committed</th>
                      <th className="text-right font-normal py-1">avg ms</th>
                    </tr>
                  </thead>
                  <tbody>
                    {actions.map((a, i) => (
                      <tr key={i} className="text-zinc-300 border-b border-zinc-900">
                        <td className="py-1">{a.action || "—"}</td>
                        <td className="text-right py-1">{a.count.toLocaleString()}</td>
                        <td className="text-right py-1 text-emerald-400">
                          {a.committed > 0 ? a.committed.toLocaleString() : "—"}
                        </td>
                        <td className="text-right py-1 text-zinc-400">
                          {a.avg_ms ? `${a.avg_ms}` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
